import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import {
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Box,
  Grid,
} from "@mui/material";

export default function CompanyFinancialInfo({
  financialData,
}) {
  // Handle both old and new data structures
  let processedFinancialData = financialData;
  
  // If financialData has a 'data' property (new API structure), extract it
  if (financialData && financialData.data) {
    processedFinancialData = financialData.data;
  }
  
  if (!processedFinancialData) return;
  
  let {
    revenue,
    isAllPRVarsInvalid,
    isAllFinanceInfoVarsInvalid,
    isAllYRVarsInvalid,
    isAllQRVarsInvalid,
    marketValue,
    operatingIncome,
    netInterestExpense,
    ebitda,
    netIncome,
    totalAssetsLastYear,
    cashAndCashEquivalents,
    totalAssets,
    equity,
    equityLastYear,
    reportDate,
    profitabilityRatios,
    quarterlyData,
    totalLiabilities,
    yearlyData,
    ROA,
    ROE,
    longTermDebt,
    shortTermDebt,
    quaterRows,
    ticker,
    market,
    funding,
    isBank,
    bankingRatios,
    nonBankingRatios,
    isAllBankingRatiosInvalid,
    isAllNonBankingRatiosInvalid
  } = processedFinancialData;
    console.log("🚀 ~ profitabilityRatios:", profitabilityRatios)

  // Extract income statement data from yearlyData if not provided separately
  var dataYear1 = "";
  var dataYear2 = "";
  var dataYear3 = "";
  var dataYear4 = "";
  
  if (yearlyData) {
    // Extract from yearlyData structure
    dataYear1 = yearlyData?.dataYear1;
    dataYear2 = yearlyData?.dataYear2;
    dataYear3 = yearlyData?.dataYear3;
    dataYear4 = yearlyData?.dataYear4;
  }
  
  const headingStyle = {
    fontSize: "15px",
  };

  const valueStyle = {
    fontSize: "14px",
    fontWeight: "600",
  };
  
  function isValid(value) {
    return (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !Number.isNaN(value)
    );
  }

  const CustomIcon = () => {
    return (
      <Box
        sx={{
          width: 30,
          height: 30,
          backgroundColor: "#FFFFFF",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.29)",
        }}
      >
        <ExpandMoreIcon style={{ color: "#5186EC" }} />
      </Box>
    );
  };

  const formatNumber = (number, currencySymbol = "US $") => {
    if (!isValid(number)) return null;
    if (
      typeof number === "string" &&
      (number.includes("US $") ||
        number.includes("$") ||
        number.includes("billion") ||
        number.includes("million") ||
        number.includes(","))
    ) {
      return number;
    }
    if (!number || isNaN(parseFloat(number))) return "";
    const num = parseFloat(number);
    
    // Determine if we need a space (for currency codes like USD, EUR) or not (for symbols like $, €)
    const needsSpace = currencySymbol && currencySymbol.length >= 3 && !currencySymbol.includes("$");
    const separator = needsSpace ? " " : "";
    
    if (num >= 1e9 || num <= -1e9) {
      return `${currencySymbol}${separator}${(num / 1e9).toFixed(2)} billion`;
    } else if (num >= 1e6 || num <= -1e6) {
      return `${currencySymbol}${separator}${(num / 1e6).toFixed(2)} million`;
    } else if (num >= 1e3 || num <= -1e3) {
      // Use Intl.NumberFormat for thousands
      return `${currencySymbol}${separator}${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    } else {
      return `${currencySymbol}${separator}${num}`;
    }
  };

  // Helper function to format structured financial data
  const formatStructuredFinancialData = (field) => {
    if (!field || !field.value) return null;
    
    // If currency is USD or not valid, just format the value
    if (!field.currencyCode || field.currencyCode === 'USD') {
      return formatNumber(field.value);
    }
    
    // If currency is different, show value with original value and date
    const formattedValue = formatNumber(field.value);
    const formattedOriginalValue = formatNumber(field.originalValue, field.currencyCode || field.currencySymbol);
    
    return `${formattedValue} (${formattedOriginalValue}, ${field.exchangeRateDate})`;
  };

  // Extract quarterly data dates
  var dataYearQ1 = "";
  var dataYearQ2 = "";
  var dataYearQ3 = "";
  var dataYearQ4 = "";

  if (quarterlyData) {
    // Handle new API structure
    dataYearQ1 = quarterlyData.fiscalDateEndingQ1 || quarterlyData.quarterlyReportsQ1?.fiscalDateEnding;
    dataYearQ2 = quarterlyData.fiscalDateEndingQ2 || quarterlyData.quarterlyReportsQ2?.fiscalDateEnding;
    dataYearQ3 = quarterlyData.fiscalDateEndingQ3 || quarterlyData.quarterlyReportsQ3?.fiscalDateEnding;
    dataYearQ4 = quarterlyData.fiscalDateEndingQ4 || quarterlyData.quarterlyReportsQ4?.fiscalDateEnding;
  }

  return (
    <div style={{ padding: "25px" }}>
      <Accordion
        width="90%"
        defaultExpanded
        style={{
          background: "#FAFAFC",
          boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
          borderRadius: "8px",
          opacity: "1",
        }}
      >
        <AccordionSummary
          expandIcon={<CustomIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography
            style={{
              width: "290px",
              height: "24px",
              fontSize: "20px",
              fontWeight: "500",
              color: "#1A2A56",
            }}
          >
            Finance Info
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container
            rowGap={2}
            display={isAllFinanceInfoVarsInvalid ? "none" : ""}
          >
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(revenue?.year1?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Revenue{" "}
                  {isValid(revenue?.year3) && isValid(revenue?.year2)
                    ? "(Last 3 years)"
                    : ""}
                  :
                </Typography>
                <Typography
                  style={{
                    color: "#2F3D63",
                    ...valueStyle,
                  }}
                >
                  {revenue?.year3 && revenue?.year2
                    ? `${formatStructuredFinancialData(revenue.year1)}, ${formatStructuredFinancialData(revenue.year2)}, ${formatStructuredFinancialData(revenue.year3)}`
                    : formatStructuredFinancialData(revenue?.year1)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(operatingIncome?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Operating Income:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(operatingIncome)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(totalLiabilities?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Total Liabilities:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(totalLiabilities)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4} md={4} display={reportDate ? "" : "none"}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Report Date:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {reportDate}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(totalAssets?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Total Assets:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(totalAssets)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(totalAssetsLastYear?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Total Assets (Last year):
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(totalAssetsLastYear)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(marketValue?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Market Value:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(marketValue)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(netInterestExpense?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Net Interest Expense:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(netInterestExpense)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(ebitda?.value) ? "block" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  EBITDA:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(ebitda)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4} md={4} display={netIncome ? "" : "none"}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Net Income:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(netIncome)}
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(cashAndCashEquivalents?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Cash And Cash Equivalents:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(cashAndCashEquivalents)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4} md={4} display={equity ? "" : "none"}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Equity:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(equity)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(equityLastYear?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Equity (Last year):
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(equityLastYear)}
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(shortTermDebt?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Short Term Debt:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(shortTermDebt)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(longTermDebt?.value) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  style={{
                    ...headingStyle,
                    color: "#777A82",
                  }}
                >
                  Long Term Debt:
                </Typography>
                <Typography
                  style={{
                    ...valueStyle,
                    color: "#2F3D63",
                  }}
                >
                  {formatStructuredFinancialData(longTermDebt)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              display={isValid(ticker) ? "" : "none"}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography style={{ ...headingStyle, color: "#777A82" }}>
                  Ticker:
                </Typography>
                <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                  {ticker} {market ? `(${market})` : ""}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: isAllPRVarsInvalid ? "none" : "block" }}>
            <Typography
              style={{
                width: "290px",
                height: "24px",
                fontSize: "20px",
                fontWeight: "500",
                color: "#1A2A56",
                padding: "20px",
              }}
              marginY={4}
            >
              Financial Ratios
            </Typography>
            <Grid container rowGap={2} ml={3}>
              <Grid
                item
                xs={12}
                sm={3}
                md={3}
                display={
                  !(operatingIncome?.value && revenue?.year1?.value) ||
                  isNaN(operatingIncome.value / revenue.year1.value)
                    ? "none"
                    : "block"
                }
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography style={{ ...headingStyle, color: "#777A82" }}>
                    Operating income/ Revenue
                  </Typography>
                  <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                  {profitabilityRatios?.operatingIncomeToRevenue}%
                  </Typography>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
                md={3}
                display={
                  !(netIncome?.value && revenue?.year1?.value) || 
                  isNaN(netIncome.value / revenue.year1.value)
                    ? "none"
                    : "block"
                }
              >
                <Box>
                  <Typography style={{ ...headingStyle, color: "#777A82" }}>
                    Net income/ Revenue:
                  </Typography>
                  <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                   {profitabilityRatios?.netIncomeToRevenue}%
                  </Typography>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
                md={3}
                display={
                  !profitabilityRatios?.cashRatio || isNaN(profitabilityRatios?.cashRatio) || profitabilityRatios?.cashRatio === "0.00" ? "none" : "block"
                }
              >
                <Box>
                  <Typography style={{ ...headingStyle, color: "#777A82" }}>
                    Cash Ratio:
                  </Typography>
                  <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                    {profitabilityRatios?.cashRatio}
                  </Typography>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
                md={3}
                display={
                  profitabilityRatios?.ROA === undefined || profitabilityRatios?.ROA === null || isNaN(profitabilityRatios?.ROA) ? "none" : "block"
                }
              >
                <Box>
                  <Typography style={{ ...headingStyle, color: "#777A82" }}>
                    ROA:
                  </Typography>
                  <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                    {profitabilityRatios?.ROA}%
                  </Typography>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
                md={3}
                display={
                  profitabilityRatios?.ROE === undefined || profitabilityRatios?.ROE === null || isNaN(profitabilityRatios?.ROE) ? "none" : "block"
                }
              >
                <Box>
                  <Typography style={{ ...headingStyle, color: "#777A82" }}>
                    ROE:
                  </Typography>
                  <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                    {profitabilityRatios?.ROE}%
                  </Typography>
                </Box>
              </Grid>
              {isBank && !isAllBankingRatiosInvalid && (
                <>
                  <Grid item xs={12} sm={3} md={3} display={bankingRatios?.capitalAdequacyRatio && bankingRatios?.capitalAdequacyRatio !== "" ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Capital Adequacy Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                      {bankingRatios?.capitalAdequacyRatio !== undefined && bankingRatios?.capitalAdequacyRatio !== null && bankingRatios?.capitalAdequacyRatio !== ""
                        ? `${Number(bankingRatios.capitalAdequacyRatio).toFixed(2)}%`
                        : "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={bankingRatios?.costToIncomeRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Cost to Income Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {bankingRatios?.costToIncomeRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={bankingRatios?.capitalizationRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Capitalization Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {bankingRatios?.capitalizationRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={bankingRatios?.loanToDepositRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Loan to Deposit Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {bankingRatios?.loanToDepositRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={bankingRatios?.lossProvisionRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Loss Provision Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {bankingRatios?.lossProvisionRatio}%
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
              {!isAllNonBankingRatiosInvalid && (
                <>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.currentRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Current Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.currentRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.assetStructureRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Asset Structure Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.assetStructureRatio}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.leverageRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Leverage Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.leverageRatio}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.interestCoverageRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Interest Coverage Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.interestCoverageRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.assetTurnoverRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Asset Turnover Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.assetTurnoverRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.revenueToLiabilitiesRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Revenue to Liabilities Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.revenueToLiabilitiesRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.assetToEquityRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Asset to Equity Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.assetToEquityRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.debtToEquityRatio ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Debt to Equity Ratio:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.debtToEquityRatio}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.grossMargin ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Gross Margin:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.grossMargin === "N/A" ? (
                          <Box display="flex" alignItems="center">
                            N/A
                            <Tooltip title="Revenue less than threshold for meaningful margin calculations" arrow>
                               <InfoOutlinedIcon style={{ color: "#777A82", fontSize: "16px", marginLeft: "5px", cursor: "pointer" }} />
                            </Tooltip>
                          </Box>
                        ) : `${nonBankingRatios?.grossMargin}%`}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.operatingMargin ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Operating Margin:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.operatingMargin === "N/A" ? (
                          <Box display="flex" alignItems="center">
                            N/A
                            <Tooltip title="Revenue less than threshold for meaningful margin calculations" arrow>
                               <InfoOutlinedIcon style={{ color: "#777A82", fontSize: "16px", marginLeft: "5px", cursor: "pointer" }} />
                            </Tooltip>
                          </Box>
                        ) : `${nonBankingRatios?.operatingMargin}%`}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3} display={nonBankingRatios?.netMargin ? "block" : "none"}>
                    <Box>
                      <Typography style={{ ...headingStyle, color: "#777A82" }}>
                        Net Margin:
                      </Typography>
                      <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                        {nonBankingRatios?.netMargin === "N/A" ? (
                          <Box display="flex" alignItems="center">
                            N/A
                            <Tooltip title="Revenue less than threshold for meaningful margin calculations" arrow>
                               <InfoOutlinedIcon style={{ color: "#777A82", fontSize: "16px", marginLeft: "5px", cursor: "pointer" }} />
                            </Tooltip>
                          </Box>
                        ) : `${nonBankingRatios?.netMargin}%`}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
            {/* Disclaimer for pre-revenue/early-stage entities - Removing bottom disclaimer as per tooltip implementation request or keeping it? User said "cant see any tooltip". Safer to use tooltip inline. Removing bottom block to avoid redundancy if tooltips are present. OR keeping it for visibility? "simple note on info icon" implies inline. I will remove the bottom block to cleaner UI. */}
          </Box>
          
          <Box sx={{ display: funding ? "block" : "none" }}>
            <Typography
              style={{
                width: "290px",
                height: "24px",
                fontSize: "20px",
                fontWeight: "500",
                color: "#1A2A56",
                padding: "20px",
              }}
              marginY={4}
            >
              Funding Information
            </Typography>
            <Grid container rowGap={2} ml={3}>
              {funding?.totalRounds && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography style={{ ...headingStyle, color: "#777A82" }}>
                      Total Rounds:
                    </Typography>
                    <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                      {funding.totalRounds}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {funding?.lastRound?.type && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography style={{ ...headingStyle, color: "#777A82" }}>
                      Last Round Type:
                    </Typography>
                    <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                      {funding.lastRound.type}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {funding?.lastRound?.date && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography style={{ ...headingStyle, color: "#777A82" }}>
                      Last Round Date:
                    </Typography>
                    <Typography style={{ ...valueStyle, color: "#2F3D63" }}>
                      {funding.lastRound.date}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {funding?.investors?.length > 0 && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography style={{ ...headingStyle, color: "#777A82" }}>
                      Investors:
                    </Typography>
                    {funding?.investors.map((investor, index) => (
                      <Typography
                        key={index}
                        style={{ ...valueStyle, color: "#2F3D63" }}
                      >
                        {investor.name} {/* Display only the investor's name */}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          
          <Box padding="25px" display={isAllYRVarsInvalid ? "none" : ""}>
            <Typography
              style={{
                width: "290px",
                height: "24px",
                fontSize: "20px",
                fontWeight: "500",
                color: "#1A2A56",
              }}
              marginBottom={2}
            >
              Yearly Review
            </Typography>
            <Box
              style={{
                background: "#FFFFFF 0% 0% no-repeat padding-box",
                boxShadow: "0px 3px 6px #00000029",
                border: "1px solid #F9F9F9",
                borderRadius: "8px",
                opacity: 1,
                padding: "25px",
              }}
            >
              <TableContainer style={{ borderRadius: "8px" }}>
                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                  <TableHead
                    style={{
                      background: "#80AAC11A",
                      borderRadius: "8px",
                      opacity: 1,
                      fontFamily: "Rubik , sans-serif",
                      fontSize: "12px",
                    }}
                  >
                    <TableRow>
                      <TableCell
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        Breakdown
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYear1?.fiscalDateEnding}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYear2?.fiscalDateEnding}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYear3?.fiscalDateEnding}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYear4?.fiscalDateEnding}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {yearlyData?.rows?.map((row) => (
                      <TableRow
                        key={row.name}
                        style={{
                          background: "#FAFAFC",
                          borderRadius: "8px",
                          opacity: 1,
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.year1 ? row.year1 : null}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.year2 ? row.year2 : null}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.year3 ? row.year3 : null}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.year4 ? row.year4 : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
          
          <Box padding="25px" display={isAllQRVarsInvalid ? "none" : ""}>
            <Typography
              style={{
                width: "290px",
                height: "24px",
                fontSize: "20px",
                fontWeight: "500",
                color: "#1A2A56",
              }}
              marginBottom={2}
            >
              Quaterly Review
            </Typography>
            <Box
              style={{
                background: "#FFFFFF 0% 0% no-repeat padding-box",
                boxShadow: "0px 3px 6px #00000029",
                border: "1px solid #F9F9F9",
                borderRadius: "8px",
                opacity: 1,
                padding: "25px",
              }}
            >
              <TableContainer style={{ borderRadius: "8px" }}>
                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                  <TableHead
                    style={{
                      background: "#80AAC11A",
                      borderRadius: "8px",
                      opacity: 1,
                      fontFamily: "Rubik , sans-serif",
                      fontSize: "12px",
                    }}
                  >
                    <TableRow>
                      <TableCell
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        Breakdown
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYearQ1}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYearQ2}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYearQ3}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ color: "#1A2A56", borderBottom: "none" }}
                      >
                        {dataYearQ4}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quarterlyData?.rows?.map((row) => (
                      <TableRow
                        key={row.name}
                        style={{
                          background: "#FAFAFC",
                          borderRadius: "8px",
                          opacity: 1,
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.Q1 ? row.Q1 : null}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.Q2 ? row.Q2 : null}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.Q3 ? row.Q3 : null}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ color: "#1A2A56", borderBottom: "none" }}
                        >
                          {row.Q4 ? row.Q4 : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}